import { useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import approveImage from 'src/assets/imgs/confirmImage.png';
import rejectImage from 'src/assets/imgs/warningImage.png';
import { If } from 'src/components/If';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  isMonitorPlanLimitError,
  UpgradeModal,
} from 'src/pages/Monitor/components/UpgradeModal';
import { useMonitorLimits } from 'src/pages/Monitor/hooks/useMonitorLimits';
import { WalletInvitationLinkStatus } from 'src/services/invite-monitor';
import {
  approveOrRejectPendingWallet,
  getPendingWalletsByOwnerId,
} from 'src/services/wallet';
import { Wallet } from 'src/types/wallet';
import { getStrapiValidationMessage } from 'src/utils/getStrapiValidationMessage';

import { ApproveOrRejectPendingWalletDialog } from './ApproveOrRejectPendingWalletDialog';
import { Filters } from './Filters';
import { OwnerGuestWalletCard } from './OwnerGuestWalletCard';

export function PendingWalletsTab() {
  const [searchName, setSearchName] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const [selectedWalletName, setSelectedWalletName] = useState<string | null>(
    null,
  );
  const { auth } = useAuthContext();
  const { limits } = useMonitorLimits();
  const queryClient = useQueryClient();
  const [approvalUpgradeModal, setApprovalUpgradeModal] = useState<{
    open: boolean;
    apiMessage: string | null;
  }>({ open: false, apiMessage: null });

  const [actionsToApproveOrReject, setActionsToApproveOrReject] = useState<{
    open: boolean;
    action: 'accepted' | 'rejected';
    invalidateQueries: string[][];
    image: string;
  }>({
    open: false,
    action: 'accepted',
    invalidateQueries: [],
    image: '',
  });

  const {
    data: wallets,
    isLoading: isLoadingWallets,
    isFetching: isFetchingWallets,
  } = useQuery({
    queryKey: ['owner-pending-wallets', searchName, orderBy],
    queryFn: () =>
      getPendingWalletsByOwnerId({
        name: searchName,
        sort: [orderBy],
        page: 1,
        pageSize: 500,
      }),
    onError: (error: Error) => {
      toast.error(error.message);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const isLoadingOrFetchingWallets = isLoadingWallets || isFetchingWallets;

  const {
    mutate: approveOrRejectPendingWalletMutation,
    isLoading: isLoadingApproveOrRejectPendingWallet,
  } = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: 'accepted' | 'rejected';
    }) => approveOrRejectPendingWallet(id, action),
    onSuccess: () => {
      toast.success('Ação realizada com sucesso');
      setSelectedWalletId(null);
      setSelectedWalletName(null);
      setActionsToApproveOrReject({
        ...actionsToApproveOrReject,
        open: false,
      });

      actionsToApproveOrReject.invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });
    },
    onError: (
      error: unknown,
      variables: { id: string; action: 'accepted' | 'rejected' },
    ) => {
      const message = getStrapiValidationMessage(
        error as Parameters<typeof getStrapiValidationMessage>[0],
        '',
      );

      if (variables.action === 'accepted' && isMonitorPlanLimitError(message)) {
        setApprovalUpgradeModal({ open: true, apiMessage: message });
        return;
      }

      toast.error(
        message || 'Não foi possível concluir a ação. Tente novamente.',
      );
    },
  });

  const handleConfirmApproveOrRejectPendingWallet = (
    action: 'accepted' | 'rejected',
    walletId: string,
    walletName: string,
  ) => {
    setActionsToApproveOrReject({
      open: true,
      action: action,
      invalidateQueries: [['owner-pending-wallets', searchName, orderBy]],
      image: action === 'accepted' ? approveImage : rejectImage,
    });
    setSelectedWalletId(walletId);
    setSelectedWalletName(walletName);
  };

  return (
    <>
      <Filters
        title=""
        searchEntityName="wallet"
        onSearch={(value) => setSearchName(value)}
        onOrderBy={(value) => setOrderBy(value)}
        orderBy={orderBy}
        useCriticalityFilters={false}
      />

      <If condition={isLoadingOrFetchingWallets}>
        <Box
          width="100%"
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      </If>

      <If condition={!wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Typography variant="h5" color="GrayText">
            Não encontramos carteiras
          </Typography>
        </Box>
      </If>

      <If condition={wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          px={4}
          py={2}
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          flexWrap="wrap"
          alignItems="start"
          gap={1.5}>
          {wallets?.map((wallet: Wallet) => (
            <OwnerGuestWalletCard
              key={wallet.id}
              walletId={wallet.id}
              ownerId={wallet.owner.id}
              name={wallet.name}
              ownerName={wallet.owner.fullName}
              guestName={wallet.creator?.fullName}
              fundsCount={wallet.walletFunds.length}
              quotasCount={wallet.walletFunds.reduce(
                (acc, fund) => acc + (fund.quotas?.length || 0),
                0,
              )}
              walletInvitationLinkStatus={
                wallet.status as WalletInvitationLinkStatus
              }
              onAcceptApproval={() =>
                handleConfirmApproveOrRejectPendingWallet(
                  'accepted',
                  wallet.id.toString(),
                  wallet.name,
                )
              }
              onRejectApproval={() =>
                handleConfirmApproveOrRejectPendingWallet(
                  'rejected',
                  wallet.id.toString(),
                  wallet.name,
                )
              }
              isOwner={auth.user?.id === wallet.owner.id}
            />
          ))}
        </Box>
      </If>

      <ApproveOrRejectPendingWalletDialog
        open={actionsToApproveOrReject.open}
        onClose={() =>
          setActionsToApproveOrReject({
            ...actionsToApproveOrReject,
            open: false,
          })
        }
        onConfirm={() => {
          approveOrRejectPendingWalletMutation({
            id: selectedWalletId?.toString() || '',
            action: actionsToApproveOrReject.action,
          });
          setActionsToApproveOrReject({
            ...actionsToApproveOrReject,
            open: false,
          });
          setSelectedWalletId(null);
          setSelectedWalletName(null);
        }}
        walletName={selectedWalletName || ''}
        action={actionsToApproveOrReject.action}
        invalidateQueries={actionsToApproveOrReject.invalidateQueries}
        image={actionsToApproveOrReject.image}
        isLoading={isLoadingApproveOrRejectPendingWallet}
      />

      <UpgradeModal
        open={approvalUpgradeModal.open}
        onClose={() =>
          setApprovalUpgradeModal({ open: false, apiMessage: null })
        }
        type="approval"
        maxLimit={limits.maxWallets}
        currentCount={limits.usageWallets}
        apiMessage={approvalUpgradeModal.apiMessage}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />
    </>
  );
}
